import styled from '@emotion/styled';
import * as Sentry from '@sentry/browser';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { Dispatch } from 'src/config/rootStore';
import { getUserInformation } from 'src/modules/user/selectors/userSelectors';
import { ContactUsModal } from 'src/shared/components/ContactUsModal';
import { color, font } from 'src/shared/styles/variables';
import { Icon, pxToRem } from '@modernhealth/vital-components';

const Wrapper = styled.div({
    maxWidth: 1440,
    display: 'flex',
    position: 'relative',
    justifyContent: 'center',
    width: '100%',
    margin: '0 auto',
    backgroundColor: color.texasRose,
});

const TextWrapper = styled.div({
    display: 'flex',
    alignItems: 'center',
    padding: '14px 0',
    position: 'relative',
});

const MainText = styled.p({
    textAlign: 'center',
    color: color.white,
    ...font.book(font.size.base),
    marginLeft: pxToRem(16),
});

const ResendText = styled.span({
    color: color.white,
    marginLeft: 5,
    textDecoration: 'underline',
    cursor: 'pointer',
    ...font.heavy(font.size.base),
});

export enum VerifyEmailAddressBannerTestID {
    ContactUsModal = 'VerifyEmailAddressBanner.ContactUsModal',
}

export const VerifyEmailAddressBanner: React.FC = () => {
    const [verificationEmailResent, setVerificationEmailResent] = useState(false);
    const [contactUsModalVisible, setContactUsModalVisible] = useState(false);

    const { t } = useTranslation();

    const user = useSelector(getUserInformation);

    const dispatch = useDispatch<Dispatch>();

    const handleStillHavingTroubleClick = () => {
        setContactUsModalVisible(true);
    };

    const handleResendVerificationEmail = async () => {
        try {
            setVerificationEmailResent(true);
            await dispatch.user.updateEmailAddress(user.newEmailAddressNotConfirmed ?? '');
        } catch (error) {
            Sentry.captureException(error);
        }
    };

    return (
        <Wrapper data-testid="verifyEmailBanner">
            <TextWrapper>
                <Icon name="warning-enclosed-filled" color="white" size={font.size.largeExtended} />
                <MainText>
                    {t('dashboard:verify_email.email_sent_to', {
                        email: user.newEmailAddressNotConfirmed,
                    })}
                </MainText>
                <ResendText
                    onClick={
                        verificationEmailResent
                            ? handleStillHavingTroubleClick
                            : handleResendVerificationEmail
                    }
                >
                    {t(
                        `dashboard:verify_email.${
                            verificationEmailResent ? 'having_trouble' : 'resend_email'
                        }`,
                    )}
                </ResendText>
            </TextWrapper>

            <ContactUsModal
                dataTestID={VerifyEmailAddressBannerTestID.ContactUsModal}
                visible={contactUsModalVisible}
                onToggle={() => setContactUsModalVisible(visible => !visible)}
                header={t('dashboard:verify_email.contact_modal.contact_us')}
                description={t('dashboard:verify_email.contact_modal.what_can_we_help_with')}
                tags={['verify_email_address_banner_contact_us_modal']}
                helpDesk
            />
        </Wrapper>
    );
};
